if (typeof StoriesComponent !== 'function') {
	class StoriesComponent extends HTMLElement {
		constructor() {
			super();

			// Selectors
			this.selectors = {
				splideWrapper: `#${this.dataset.id}.component-stories__splide`,
				splideSlide: '.preview-block',
				storiesComponent: '.component-stories',
				storiesHeading: '.component-stories__heading',
				storySlide: '.story__slide',
				storySlideVisible: '.story__slide--visible',
				storySlideMedia: '.story-slide__media',
				storiesModal: '.component-stories__modal',
				storiesModalOverlay: '.stories-modal__overlay',
				modalStory: '.stories-modal__story',
				modalStoryVisible: '.stories-modal__story--visible',
				modalStoryById: '.stories-modal__story[data-id="[[storyId]]"]',
				modalButtonClose: '.story-actions__button--close',
				modalButtonPlay: '.story-actions__button--play',
				modalButtonVolume: '.story-actions__button--volume',
				modalStoryProgressBars: '.story-actions__progress-bars',
				progressBar: '.progress-bars__bar',
				progressBarActive: '.progress-bars__bar--active',
				modalStorySlides: '.story__slides',
				modalStoryIcon: '.story-actions__icon',
				modalControllers: '.stories-modal__controller',
				cartDrawer: 'drawer-component[data-type="cart"]',
			};

			// Classes
			this.classes = {
				storiesFullWidth: 'full-width',
				storiesNoMarginRight: 'no-margin-right',
				storiesContainer: 'wrapper',
				headingBlock: 'component-stories--heading-block',
				storiesModalVisible: 'component-stories__modal--visible',
				modalStory: 'stories-modal__story',
				modalStoryVisible: 'stories-modal__story--visible',
				storySlideVisible: 'story__slide--visible',
				storyModalPaused: 'stories-modal__story--paused',
				modalButtonVolumeMuted: 'story-actions__button--volume-muted',
				modalButtonPlayPaused: 'story-actions__button--play-paused',
				slideMediaImage: 'slide-media__image',
				progressBarActive: 'progress-bars__bar--active',
				progressBarFilled: 'progress-bars__bar--filled',
				progressBarPaused: 'progress-bars__bar--paused',
				modalController: 'stories-modal__controller',
				modalControllerPaused: 'stories-modal__controller--paused',
				modalControllerNext: 'stories-modal__controller--next',
				modalControllerDisabled: 'stories-modal__controller--disabled',
				bodyBlockedScroll: 'blocked-scroll',
				buttonLoading: 'button--loading',
			};

			// Elements
			this.elements = {
				storiesComponent: this.querySelector(this.selectors.storiesComponent),
				splideWrapper: this.querySelector(this.selectors.splideWrapper),
				storiesModal: this.querySelector(this.selectors.storiesModal),
				controllers: this.querySelectorAll(this.selectors.modalControllers),
				cartDrawer: document.querySelector(this.selectors.cartDrawer),
			};

			// Flags
			this.isMobile = /iPhone|iPad|iPod|Android/i.test(navigator.userAgent);
			this.audioMuted = true;
			this.storiesPlaying = false;

			// Variables
			this.storiesCount = +this.elements.splideWrapper.dataset.storiesCount;
			this.storyPreviewWidth = this.querySelector(this.selectors.splideSlide)?.offsetWidth + 20 || 0;

			// Timers
			this.storyTimer = null;
			this.touchTimer = null;
			this.autoPlayTimer = null;

			// Touch state
			this.touch = {
				startX: 0,
				startY: 0,
				blockSwipe: false,
			};

			// Methods
			this.init();
		}

		init() {
			this.initSplide();
			this.collectStoriesMarkup();
			this.cleanEmptySlides();
			this.addEventListeners();
		}

		addEventListeners() {
			const { storiesComponent, storiesModal, controllers } = this.elements;

			document.addEventListener('keydown', (event) => {
				if (
					(event.key === 'Escape' || event.keyCode === 27) &&
					storiesModal.classList.contains(this.classes.storiesModalVisible)
				) {
					this.closeStoryModal();
				}
			});

			this.querySelectorAll(this.selectors.splideSlide).forEach((slide) => {
				slide.addEventListener('click', () => this.initStoriesModal(slide.dataset.id));
				slide.addEventListener('keydown', (event) => {
					if (event.keyCode === 32 || event.key === ' ' || event.key === 'Spacebar') {
						this.initStoriesModal(slide.dataset.id);
					}
				});
			});

			if (storiesComponent.classList.contains(this.classes.headingBlock)) {
				this.querySelector(this.selectors.storiesHeading).addEventListener('click', () => {
					this.initStoriesModal(this.querySelector(this.selectors.splideSlide).dataset.id);
				});
			}

			this.querySelectorAll(this.selectors.modalButtonClose).forEach((button) => {
				button.addEventListener('click', () => this.closeStoryModal());
			});

			this.querySelectorAll(this.selectors.modalButtonPlay).forEach((button) => {
				button.addEventListener('click', () => {
					const storyId = button.closest(this.selectors.modalStory).dataset.id;

					this.toggleStoryPlay(button, storyId);
				});
			});

			this.querySelectorAll(this.selectors.modalButtonVolume).forEach((button) => {
				button.addEventListener('click', () => {
					const storyId = button.closest(this.selectors.modalStory).dataset.id;
					const muted = button.classList.contains(this.classes.modalButtonVolumeMuted);

					this.toggleStoriesAudio(muted);
				});
			});

			this.querySelector(this.selectors.storiesModalOverlay).addEventListener('click', () =>
				this.closeStoryModal()
			);

			storiesModal.addEventListener('story:slide:played', (event) => {
				const { storyId, slideIndex } = event.detail;
				const currentStory = this.querySelector(this.selectors.modalStoryById.replace('[[storyId]]', storyId));
				const currentStorySlides = currentStory.querySelector(
					this.selectors.modalStorySlides
				).childElementCount;

				this.autoPlayTimer = setTimeout(() => {
					if (slideIndex < currentStorySlides - 1) {
						this.showStorySlide(storyId, slideIndex + 1);
					} else {
						const nextStory = currentStory.nextElementSibling;

						if (nextStory) {
							this.showStoryById(nextStory.dataset.id);
							this.showStorySlide(nextStory.dataset.id, 0);
						} else {
							this.closeStoryModal();
						}
					}
				}, 350);
			});

			controllers.forEach((controller) => {
				const { modalControllerNext } = this.classes;

				controller.addEventListener('click', () => {
					const playNext = controller.classList.contains(modalControllerNext);

					if (storiesModal.classList.contains(this.classes.storiesModalVisible)) {
						this.playStory(playNext);
					}
				});
			});

			this.querySelectorAll(this.selectors.storySlide).forEach((slide) => {
				slide.addEventListener('touchstart', (event) => {
					setTimeout(
						() => {
							this.touchStart(event, slide.dataset.id);
						},
						1,
						{ passive: true }
					);
				});

				slide.addEventListener('touchend', (event) => {
					setTimeout(
						() => {
							this.touchEnd(event, slide.dataset.id);
						},
						1,
						{ passive: true }
					);
				});

				slide.addEventListener('contextmenu', (event) => event.preventDefault());

				if (!this.isMobile) {
					slide.addEventListener('click', (event) => {
						const parentStory = slide.closest(this.selectors.modalStory);
						const storyId = parentStory.dataset.id;
						const playButton = parentStory.querySelector(this.selectors.modalButtonPlay);

						if (this.storiesPlaying) {
							this.toggleStoryPlay(null, storyId);
							playButton.classList.toggle(this.classes.modalButtonPlayPaused);
						}
					});
				}
			});

			document.addEventListener('cart-drawer:load', (event) => {
				this.closeStoryModal();
			});
		}

		playStory(isNext) {
			const { modalStorySlides, modalStoryVisible, storySlideVisible } = this.selectors;
			const { modalController } = this.classes;
			const { storiesModal } = this.elements;

			const currentStory = storiesModal.querySelector(modalStoryVisible);
			const currentStorySlides = Array.prototype.slice.call(
				currentStory.querySelector(modalStorySlides).children
			);
			const currentSlide = currentStory.querySelector(storySlideVisible);
			const currentSlideIndex = currentStorySlides.indexOf(currentSlide);

			this.storyTimer?.reset();
			window.clearTimeout(this.autoPlayTimer);
			this.autoPlayTimer = null;
			this.storyTimer = null;

			this.querySelectorAll('video').forEach((video) => {
				video.pause();
			});
			this.clearStoriesMedia();

			if (isNext) {
				const isLastSlide = currentSlideIndex === currentStorySlides.length - 1;
				const nextStory = currentStory.nextElementSibling;

				if (isLastSlide && !nextStory) {
					this.closeStoryModal();
				} else if (isLastSlide && nextStory) {
					this.showStoryById(nextStory.dataset.id);
					this.showStorySlide(nextStory.dataset.id, 0);
				} else {
					this.showStorySlide(currentStory.dataset.id, currentSlideIndex + 1);
				}
			} else {
				const isFirstSlide = currentSlideIndex === 0;
				const isFirstStory = currentStory.previousElementSibling.classList.contains(modalController);
				const prevStory = currentStory.previousElementSibling;

				if (isFirstStory) {
					if (isFirstSlide) {
						this.closeStoryModal();
					} else {
						this.showStorySlide(currentStory.dataset.id, currentSlideIndex - 1);
					}
				} else {
					if (isFirstSlide) {
						this.showStoryById(prevStory.dataset.id);
						this.showStorySlide(prevStory.dataset.id, 0);
					} else {
						this.showStorySlide(currentStory.dataset.id, currentSlideIndex - 1);
					}
				}
			}
		}

		touchStart(event, storyId) {
			this.touch.startX = event.changedTouches[0].screenX;
			this.touch.startY = event.changedTouches[0].screenY;
			this.touch.startTime = new Date().getTime();

			event.preventDefault();
			event.stopPropagation();

			this.touchTimer = setTimeout(() => {
				if (this.isMobile && this.storiesPlaying) {
					const targetStory = this.querySelector(
						this.selectors.modalStoryById.replace('[[storyId]]', storyId)
					);

					this.blockSwipe = true;
					targetStory.classList.add(this.classes.storyModalPaused);
					this.toggleStoryPlay(null, storyId);
					this.elements.controllers.forEach((controller) => {
						controller.classList.add(this.classes.modalControllerPaused);
					});
				}
			}, 500);
		}

		touchEnd(event, storyId) {
			const currentEndX = event.changedTouches[0].screenX;
			const currentEndY = event.changedTouches[0].screenY;
			const currentTime = new Date().getTime();
			const xDiff = currentEndX - this.touch.startX;
			const yDiff = currentEndY - this.touch.startY;
			const threshold = 100;

			event.preventDefault();
			event.stopPropagation();

			window.clearTimeout(this.touchTimer);
			this.touchTimer = null;

			if (this.blockSwipe) {
				if (this.isMobile && !this.storiesPlaying) {
					const targetStory = this.querySelector(
						this.selectors.modalStoryById.replace('[[storyId]]', storyId)
					);

					targetStory.classList.remove(this.classes.storyModalPaused);
					this.blockSwipe = false;
					this.toggleStoryPlay(null, storyId);
					this.elements.controllers.forEach((controller) => {
						controller.classList.remove(this.classes.modalControllerPaused);
					});
				}
			} else {
				if (Math.abs(xDiff) > threshold) {
					if (xDiff > 0) {
						this.playStory(false);
					} else {
						this.playStory(true);
					}
				} else if (Math.abs(yDiff) > threshold) {
					this.closeStoryModal();
				}
			}

			this.touch.startX = 0;
			this.touch.startY = 0;
		}

		clearStoriesMedia() {
			this.querySelectorAll(this.selectors.storySlideMedia).forEach((media) => {
				media.innerHTML = '';
			});
		}

		initSplide() {
			const { storiesComponent, splideWrapper } = this.elements;
			const { storiesCount, storyPreviewWidth } = this;
			const { storiesFullWidth, storiesNoMarginRight, headingBlock, storiesContainer } = this.classes;
			const enableFeatures = storiesCount * storyPreviewWidth > splideWrapper.offsetWidth;

			const splideOptions = {
				perMove: 1,
				type: 'slide',
				drag: enableFeatures,
				arrows: enableFeatures,
				pagination: false,
				gap: '20px',
				autoWidth: true,
				autoHeight: true,
			};

			if (
				storiesComponent.classList.contains(storiesFullWidth) ||
				(storiesComponent.classList.contains(storiesNoMarginRight) &&
					storiesComponent.classList.contains(headingBlock))
			) {
				splideOptions.padding = { left: '20px', right: '20px' };
			} else if (
				storiesComponent.classList.contains(headingBlock) &&
				storiesComponent.classList.contains(storiesContainer)
			) {
				splideOptions.padding = { left: '20px', right: '0px' };
			}

			new Splide(splideWrapper, splideOptions).mount();
		}

		toggleStoriesAudio(muted) {
			const { audioMuted } = this;
			const { modalButtonVolumeMuted } = this.classes;

			this.audioMuted = !audioMuted;

			this.querySelectorAll(this.selectors.modalButtonVolume).forEach((button) => {
				button.classList.toggle(modalButtonVolumeMuted);
				button.ariaPressed = button.classList.contains(modalButtonVolumeMuted);
			});

			this.querySelectorAll('video').forEach((video) => {
				video.muted = !audioMuted;
			});
		}

		toggleStoryPlay(element, storyId) {
			const { modalStoryById, progressBarActive } = this.selectors;
			const { modalButtonPlayPaused, progressBarPaused } = this.classes;
			const targetStory = this.querySelector(modalStoryById.replace('[[storyId]]', storyId));

			if (element) {
				element.classList.toggle(modalButtonPlayPaused);
				element.ariaPressed = element.classList.contains(modalButtonPlayPaused);
			}

			targetStory.querySelectorAll(progressBarActive).forEach((progressBar) => {
				progressBar.classList.toggle(progressBarPaused);
			});

			this.storiesPlaying ? this.storyTimer.pause() : this.storyTimer.play();
			this.storiesPlaying = !this.storiesPlaying;

			targetStory.querySelectorAll('video').forEach((video) => {
				if (video.paused) {
					video.play();
				} else {
					video.pause();
				}
			});
		}

		showStoryById(storyId) {
			const { modalStory, modalStoryById, modalStoryProgressBars, modalStorySlides, modalStoryIcon } =
				this.selectors;
			const { modalStoryVisible } = this.classes;
			const { storiesModal } = this.elements;
			const targetStory = this.querySelector(modalStoryById.replace('[[storyId]]', storyId));
			const targetStorySlidesCount = targetStory.querySelector(modalStorySlides).childElementCount;
			const storyIconSource = targetStory.dataset.iconSource;

			storiesModal.querySelectorAll(modalStory).forEach((story) => {
				story.classList.remove(modalStoryVisible);
			});
			targetStory.querySelector(modalStoryIcon).style.backgroundImage = `url(${storyIconSource})`;
			targetStory.classList.add(modalStoryVisible);
			targetStory.querySelector(modalStoryProgressBars).innerHTML =
				this.generateProgressBars(targetStorySlidesCount);
		}

		animateProgressBar(parent, index) {
			const { progressBarActive } = this.classes;
			const { progressBar } = this.selectors;
			const targetProgressBar = parent.querySelectorAll(progressBar)[index];

			targetProgressBar.classList.add(progressBarActive);
		}

		showStorySlide(storyId, slideIndex) {
			const {
				modalStoryById,
				storySlide,
				storySlideMedia,
				progressBar,
				modalStoryIcon,
				modalControllers,
				modalButtonPlay,
			} = this.selectors;

			const {
				storySlideVisible,
				slideMediaImage,
				progressBarActive,
				progressBarFilled,
				modalControllerDisabled,
				modalButtonPlayPaused,
			} = this.classes;

			const { storiesModal } = this.elements;
			const targetStory = this.querySelector(modalStoryById.replace('[[storyId]]', storyId));
			const currentSlide = targetStory.querySelectorAll(storySlide)[slideIndex];
			const mediaElement = currentSlide.querySelector(storySlideMedia);
			const mediaSource = mediaElement.dataset.src;
			const mediaType = mediaElement.dataset.type;
			const iconUrl = currentSlide.dataset.iconUrl;
			const defaultSlideTimer = storiesModal.dataset.defaultSlideTimer;

			const storySlidePlayed = new CustomEvent('story:slide:played', {
				detail: {
					storyId: storyId,
					slideIndex: slideIndex,
				},
			});

			this.storyTimer = null;

			storiesModal.querySelectorAll(modalControllers).forEach((controller) => {
				controller.classList.add(modalControllerDisabled);
			});

			this.querySelectorAll(modalButtonPlay).forEach((button) => {
				button.classList.remove(modalButtonPlayPaused);
			});

			if (iconUrl) {
				targetStory.querySelector(modalStoryIcon).classList.remove('hidden');
				targetStory.querySelector(modalStoryIcon).href = iconUrl;
			} else {
				targetStory.querySelector(modalStoryIcon).classList.add('hidden');
			}

			if (targetStory.dataset.storyType === 'product_list') {
				const storyIconSource = currentSlide.dataset.iconSource;

				targetStory.querySelector(modalStoryIcon).style.backgroundImage = `url(${storyIconSource})`;
			}

			storiesModal.querySelectorAll(storySlide).forEach((slide) => {
				slide.classList.remove(storySlideVisible);
			});

			this.clearStoriesMedia();

			if (mediaType === 'image') {
				const image = new Image();

				storiesModal.style.setProperty('--slide-timer', `${defaultSlideTimer}s`);

				image.onload = () => {
					mediaElement.append(image);

					setTimeout(() => {
						storiesModal.querySelectorAll(modalControllers).forEach((controller) => {
							controller.classList.remove(modalControllerDisabled);
						});

						targetStory.querySelectorAll(progressBar).forEach((progressBar, index) => {
							progressBar.classList.remove(progressBarFilled);
							progressBar.classList.remove(progressBarActive);

							if (index < slideIndex) {
								progressBar.classList.add(progressBarFilled);
							}
						});

						targetStory.querySelectorAll(progressBar)[slideIndex].classList.add(progressBarActive);

						this.storyTimer = new Timer(() => {
							storiesModal.dispatchEvent(storySlidePlayed);
						}, defaultSlideTimer * 1000);

						this.storiesPlaying = true;
						this.storyTimer.play();
					}, 1);
				};

				image.classList.add(slideMediaImage);
				image.src = mediaSource;
			} else if (mediaType === 'video') {
				const videoElement = document.createElement('video');

				mediaElement.append(videoElement);
				videoElement.classList.add(slideMediaImage);
				videoElement.src = mediaSource;
				videoElement.muted = true;
				videoElement.playsInline = true;

				if (videoElement.readyState !== 4) {
					videoElement.load();
				}

				videoElement.addEventListener('canplaythrough', () => {
					storiesModal.style.setProperty('--slide-timer', `${videoElement.duration}s`);

					setTimeout(() => {
						videoElement.muted = this.audioMuted;
						videoElement.play();

						storiesModal.querySelectorAll(modalControllers).forEach((controller) => {
							controller.classList.remove(modalControllerDisabled);
						});

						targetStory.querySelectorAll(progressBar)[slideIndex].classList.add(progressBarActive);

						this.storyTimer = new Timer(() => {
							storiesModal.dispatchEvent(storySlidePlayed);
						}, videoElement.duration * 1000);

						this.storyTimer.play();
						this.storiesPlaying = true;
					}, 1);
				});
			}

			currentSlide.classList.add(storySlideVisible);
			setTimeout(() => {
				trapFocus(storiesModal);
			}, 100);
		}

		resetModal() {
			const { modalStory, storySlide, modalButtonPlay } = this.selectors;
			const { storiesModalVisible, modalStoryVisible, storySlideVisible, modalButtonPlayPaused } = this.classes;
			const { storiesModal } = this.elements;

			this.querySelectorAll(modalStory).forEach((story) => {
				story.classList.remove(modalStoryVisible);
				story.querySelectorAll(storySlide).forEach((slide) => {
					slide.classList.remove(storySlideVisible);
				});
			});

			this.querySelectorAll(modalButtonPlay).forEach((button) => {
				button.classList.remove(modalButtonPlayPaused);
			});

			this.querySelectorAll('video').forEach((video) => {
				video.pause();
			});

			this.clearStoriesMedia();
			this.storyTimer?.reset();
			this.storiesPlaying = false;
			window.clearTimeout(this.autoPlayTimer);
			this.autoPlayTimer = null;
			this.storyTimer = null;
		}

		closeStoryModal() {
			const { modalStory, storySlide } = this.selectors;
			const { storiesModalVisible, modalStoryVisible, storySlideVisible } = this.classes;
			const { storiesModal } = this.elements;

			storiesModal.classList.remove(storiesModalVisible);
			document.body.classList.remove(this.classes.bodyBlockedScroll);

			this.resetModal();

			setTimeout(() => {
				this.resetModal();
			}, 350);
		}

		initStoriesModal(storyId) {
			this.querySelector(this.selectors.storiesModal).classList.add(this.classes.storiesModalVisible);
			this.showStoryById(storyId);
			this.showStorySlide(storyId, 0);
			document.body.classList.add(this.classes.bodyBlockedScroll);
		}

		generateProgressBars(count) {
			let progressBars = '';

			for (let i = 0; i < count; i++) {
				progressBars += '<span class="progress-bars__bar"></span>';
			}

			return progressBars;
		}

		collectStoriesMarkup() {
			const { storySlide, modalStoryById } = this.selectors;

			this.querySelectorAll(storySlide).forEach((slide) => {
				const { modalStoryById, modalStorySlides } = this.selectors;
				const blockId = slide.dataset.id;
				const targetStory = this.querySelector(modalStoryById.replace('[[storyId]]', blockId));
				const targetStorySlides = targetStory.querySelector(modalStorySlides);

				targetStory.dataset.iconSource = slide.dataset.iconSource;
				targetStorySlides.append(slide);
			});
		}

		cleanEmptySlides() {
			const { modalStory, modalStorySlides } = this.selectors;

			this.querySelectorAll(modalStory).forEach((story) => {
				const storySlides = story.querySelector(modalStorySlides);

				if (!storySlides.childElementCount) {
					story.remove();
				}
			});
		}
	}

	if (!customElements.get('stories-component')) {
		customElements.define('stories-component', StoriesComponent);
	}
}
